import React from 'react';
import { AppUser } from '../../../types';
import { usePropertyComments } from '../../../hooks/usePropertyComments';
import { PropertyCommentItem } from './PropertyCommentItem';
import { PropertyCommentForm } from './PropertyCommentForm';
import { SpinnerIcon, ExclamationCircleIcon } from '../../Icons';

interface Props {
  propertyCode: string;
  appUser: AppUser | null;
}

export const PropertyCommentsSection: React.FC<Props> = ({ propertyCode, appUser }) => {
  const {
    comments,
    loading,
    submitting,
    error,
    editingComment,
    canAccess,
    addComment,
    updateComment,
    removeComment,
    startEditing,
    cancelEditing,
  } = usePropertyComments(propertyCode, appUser);

  if (!canAccess) return null;

  return (
    <section id="property-comments-section" className="bg-teal-50/40 border border-teal-100 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm scroll-mt-24">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-teal-100/80 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-teal-600 text-white flex items-center justify-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-black uppercase tracking-tight text-gray-900">
                Comentarios Comerciales
              </h3>
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-teal-600 text-white">
                {comments.length}
              </span>
            </div>
            <p className="text-[11px] text-teal-800 font-medium">
              Registro interno exclusivo para asesores comerciales
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-4 py-2.5 rounded-xl">
          <ExclamationCircleIcon className="w-4 h-4 shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {editingComment ? (
        <div className="bg-white border border-teal-200 rounded-2xl p-4 shadow-sm">
          <div className="text-xs font-bold text-teal-800 mb-2">Editando tu comentario:</div>
          <PropertyCommentForm
            initialValue={editingComment.content}
            isEditing
            isSubmitting={submitting}
            onSubmit={updateComment}
            onCancel={cancelEditing}
          />
        </div>
      ) : (
        <PropertyCommentForm
          isSubmitting={submitting}
          onSubmit={addComment}
        />
      )}

      <div className="space-y-3 pt-2">
        {loading ? (
          <div className="flex items-center justify-center py-6 gap-2 text-xs text-gray-500">
            <SpinnerIcon className="w-4 h-4 animate-spin text-teal-600" />
            <span>Cargando comentarios comerciales...</span>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400 border border-dashed border-teal-200 rounded-2xl bg-white/50">
            No hay comentarios comerciales registrados aún. Sé el primero en dejar una nota.
          </div>
        ) : (
          comments.map((comment) => (
            <PropertyCommentItem
              key={comment.id}
              comment={comment}
              currentUser={appUser}
              onStartEdit={startEditing}
              onDelete={removeComment}
            />
          ))
        )}
      </div>
    </section>
  );
};
